import { Download, Search } from "lucide-react";
import { DataTable } from "../_misc/data-table";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetAdminStats,
  useGetAdminDexTransactions,
  useGetAllDeposits,
  useGetCardTransactionsAdmin,
} from "@/hooks/use-queries";
import { allTransactionsColumn } from "./_misc/allTransactionColumn";
import { cardWithdrawalColumn } from "./_misc/cardColumn";
import { swapColumn } from "./_misc/swapColumn";
import LineLoader from "@/components/line-loader";
import { useState, useEffect } from "react";
import { downloadCSV } from "@/lib/helper";
import { formatAmount } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";

const tabToTypeMap: Record<string, string> = {
  "wallet-deposit": "deposit",
  "wallet-withdraw": "withdrawal",
  "card-top-up": "card-topup",
  "card-withdrawal": "card-withdrawal",
};

const TransactionsAdmin = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState("all-transactions");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [paginationParams, setPaginationParams] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [pageCount, setPageCount] = useState(1);
  const [cardPageCount, setCardPageCount] = useState(1);
  const [swapPageCount, setSwapPageCount] = useState(1);

  const isCardPurchasesTab = currentTab === "card-purchases";

  const { data: allTransactions, isFetching: isLoading } = useGetAllDeposits({
    page: paginationParams.pageIndex,
    limit: paginationParams.pageSize,
    search: debouncedSearchTerm,
    ...(currentTab !== "all-transactions" &&
      !isCardPurchasesTab && {
        type: tabToTypeMap[currentTab],
      }),
  });

  const {
    data: cardTransactions,
    isFetching: isLoadingCards,
  } = useGetCardTransactionsAdmin({
    page: paginationParams.pageIndex,
    limit: paginationParams.pageSize,
    type: "Consumption",
  });

  const { data: adminStats } = useGetAdminStats();

  const isSwapTab = currentTab === "swap";
  // Matches the user dex endpoint, which is 1-based, unlike the other admin
  // lists above which send the 0-based pageIndex.
  const { data: adminSwaps, isLoading: isLoadingSwaps } =
    useGetAdminDexTransactions(
      {
        page: paginationParams.pageIndex + 1,
        limit: paginationParams.pageSize,
        search: debouncedSearchTerm,
      },
      isSwapTab,
    );

  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    setPaginationParams({ pageIndex: 0, pageSize: 10 });
  };

  useEffect(() => {
    setPageCount(allTransactions?.totalPages || 1);
  }, [allTransactions]);

  useEffect(() => {
    if (isSwapTab) {
      setSwapPageCount(adminSwaps?.totalPages || 1);
    }
  }, [adminSwaps, isSwapTab]);

  useEffect(() => {
    const total = cardTransactions?.data?.total ?? 0;
    setCardPageCount(total ? Math.ceil(total / paginationParams.pageSize) : 1);
  }, [cardTransactions, paginationParams.pageSize]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-4">
        {/* Deposit Stats */}
        <div className="text-primary-500 flex min-w-[220px] grow flex-col justify-between gap-4 rounded-lg bg-white px-4 py-2.5 lg:grow-0">
          <div className="flex items-center justify-between">
            <img
              className="size-12"
              src="/icons/deposit-admin.svg"
              alt="master wallet icon"
            />
            <div className="flex flex-col items-end justify-between gap-2">
              <span className="leading-4 font-semibold">Deposit</span>
              <span className="text-2xl leading-4 font-medium">
                $
                {formatAmount(
                  adminStats?.stats.deposit?.completed?.totalAmount,
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-6 text-xs">
            <div>
              <p className="text-primary-green font-semibold">Available</p>
              <p className="font-bold">
                {adminStats?.stats.deposit?.completed?.count}
              </p>
            </div>
            <div>
              <p className="text-primary-brown font-semibold">Pending</p>
              <p className="font-bold">
                {adminStats?.stats.deposit?.pending?.count}
              </p>
            </div>
            <div>
              <p className="text-primary-red font-semibold">Pending</p>
              <p className="font-bold">
                {adminStats?.stats.deposit?.failed?.count ?? 0}
              </p>
            </div>
          </div>
        </div>

        {/* Withdrawal Stats */}
        <div className="text-primary-500 flex min-w-[220px] grow flex-col justify-between gap-4 rounded-lg bg-white px-4 py-2.5 lg:grow-0">
          <div className="flex items-center justify-between">
            <img
              className="size-12"
              src="/icons/withdrawal-admin.svg"
              alt="shaded user icon"
            />
            <div className="flex flex-col items-end justify-between gap-2">
              <span className="leading-4 font-semibold">Withdrawals</span>
              <span className="text-2xl leading-4 font-medium">
                $
                {formatAmount(
                  adminStats?.stats.withdrawal?.completed?.totalAmount,
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-6 text-xs">
            <div>
              <p className="text-primary-green font-semibold">Successful</p>
              <p className="font-bold">
                {adminStats?.stats.withdrawal?.completed?.count ?? 0}
              </p>
            </div>
            <div>
              <p className="text-primary-brown font-semibold">Pending</p>
              <p className="font-bold">
                {adminStats?.stats.withdrawal?.pending?.count ?? 0}
              </p>
            </div>
            <div>
              <p className="text-primary-red font-semibold">Failed</p>
              <p className="font-bold">
                {adminStats?.stats.withdrawal?.failed?.count ?? 0}
              </p>
            </div>
          </div>
        </div>

        {/* Total Fees */}
        <div className="text-primary-500 flex min-w-[220px] grow flex-col justify-between gap-4 rounded-lg bg-white px-4 py-2.5 lg:grow-0">
          <div className="flex items-center justify-between">
            <img
              className="size-12 -scale-100"
              src="/icons/withdrawal-admin.svg"
              alt="shaded user icon"
            />
            <div className="flex flex-col items-end justify-between gap-2">
              <span className="leading-4 font-semibold">Total Fees</span>
              <span className="text-2xl leading-4 font-medium">
                $
                {formatAmount(
                  adminStats?.stats["referral-reward"]?.totalAmount,
                )}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-6 text-xs">
            <div>
              <p className="text-primary-green font-semibold">Available Fee</p>
              <p className="font-bold">
                {adminStats?.stats.withdrawal?.completed?.count || 0}
              </p>
            </div>
            <div>
              <p className="text-primary-red font-semibold">Withdrawn</p>
              <p className="font-bold">
                {adminStats?.stats.withdrawal?.failed?.count || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Tabs
        className="mt-8"
        defaultValue="all-transactions"
        onValueChange={handleTabChange}
      >
        <div className="mt-4 flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <TabsList className="no-scrollbar max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="all-transactions">All Transactions</TabsTrigger>
            <TabsTrigger value="wallet-deposit">Wallet Deposit</TabsTrigger>
            <TabsTrigger value="wallet-withdraw">Wallet Withdrawal</TabsTrigger>
            <TabsTrigger value="card-top-up">Card TopUp</TabsTrigger>
            <TabsTrigger value="card-withdrawal">Card Withdrawal</TabsTrigger>
            <TabsTrigger value="card-purchases">Card Purchases</TabsTrigger>
            <TabsTrigger value="swap">Swap</TabsTrigger>
          </TabsList>
          <div className="flex gap-4">
            <div className="flex grow md:grow-0">
              <div className="relative grow rounded-md bg-white md:grow-0">
                <Search className="text-primary-50 absolute top-1/2 left-3 size-3 -translate-y-1/2" />
                <input
                  type="search"
                  className="h-full w-full rounded-md bg-white pl-8 placeholder:leading-3 md:w-auto"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <Button
              onClick={() =>
                downloadCSV(allTransactions?.data, "Transactions")
              }
              className={"flex h-auto shrink-0 items-center gap-2 rounded-lg py-2.5"}
            >
              <Download size={15} />
              <span>Download (CSV)</span>
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <TabsContent value="card-purchases">
            {isLoadingCards ? (
              <div className="h-1">
                <LineLoader />
              </div>
            ) : (
              <DataTable
                columns={cardWithdrawalColumn}
                data={cardTransactions?.data?.data ?? []}
                pagination={paginationParams}
                setPagination={setPaginationParams}
                pageCount={cardPageCount}
              />
            )}
          </TabsContent>

          <TabsContent value="swap">
            {isLoadingSwaps ? (
              <div className="h-1">
                <LineLoader />
              </div>
            ) : (
              <DataTable
                columns={swapColumn}
                data={adminSwaps?.data ?? []}
                pagination={paginationParams}
                setPagination={setPaginationParams}
                pageCount={swapPageCount}
                noDataText="No swap transactions."
              />
            )}
          </TabsContent>

          {!isCardPurchasesTab && !isSwapTab && (
            <>
              {isLoading ? (
                <div className="h-1">
                  <LineLoader />
                </div>
              ) : (
                <>
                  <TabsContent value="all-transactions">
                    <DataTable
                      columns={allTransactionsColumn}
                      data={allTransactions?.data ?? []}
                      pagination={paginationParams}
                      setPagination={setPaginationParams}
                      pageCount={pageCount}
                    />
                  </TabsContent>
                  <TabsContent value="wallet-deposit">
                    <DataTable
                      columns={allTransactionsColumn}
                      data={allTransactions?.data ?? []}
                      pagination={paginationParams}
                      setPagination={setPaginationParams}
                      pageCount={pageCount}
                    />
                  </TabsContent>
                  <TabsContent value="wallet-withdraw">
                    <DataTable
                      columns={allTransactionsColumn}
                      data={allTransactions?.data ?? []}
                      pagination={paginationParams}
                      setPagination={setPaginationParams}
                      pageCount={pageCount}
                    />
                  </TabsContent>
                  <TabsContent value="card-top-up">
                    <DataTable
                      columns={allTransactionsColumn}
                      data={allTransactions?.data ?? []}
                      pagination={paginationParams}
                      setPagination={setPaginationParams}
                      pageCount={pageCount}
                    />
                  </TabsContent>
                  <TabsContent value="card-withdrawal">
                    <DataTable
                      columns={allTransactionsColumn}
                      data={allTransactions?.data ?? []}
                      pagination={paginationParams}
                      setPagination={setPaginationParams}
                      pageCount={pageCount}
                    />
                  </TabsContent>
                </>
              )}
            </>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default TransactionsAdmin;
